import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import Icon from '../../../components/AppIcon';

const StoriesBar = ({ currentUser }) => {
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeStoryGroup, setActiveStoryGroup] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('photo');
  const [uploading, setUploading] = useState(false);

  // Fallback state if convex isn't fully configured
  const [localStories, setLocalStories] = useState([
    {
      userId: 'system',
      user: { fullName: 'Aether Team', avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb' },
      stories: [{ id: 1, mediaType: 'photo', mediaUrl: 'https://images.unsplash.com/photo-1506744626753-1fa28f673b0c' }]
    }
  ]);

  let activeStories = [];
  try {
    const convexStories = useQuery(api.stories.getActiveStories);
    activeStories = convexStories !== undefined ? convexStories : localStories;
  } catch (e) {
    activeStories = localStories;
  }

  let createStoryMut = async () => {};
  try {
    const convexCreate = useMutation(api.stories.createStory);
    createStoryMut = convexCreate;
  } catch (e) {
    createStoryMut = async (args) => {
      setLocalStories([{
        userId: args.userId,
        user: { fullName: currentUser?.name || 'You', avatarUrl: currentUser?.avatar },
        stories: [{ id: Date.now(), mediaType: args.mediaType, mediaUrl: args.mediaUrl }]
      }, ...localStories]);
    };
  }

  const handleCreate = async () => {
    if (!mediaUrl) return;
    setUploading(true);
    try {
      await createStoryMut({
        userId: currentUser.id,
        mediaUrl,
        mediaType
      });
      setCreatorOpen(false);
      setMediaUrl('');
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full mb-stack-lg">
      <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar">
        {/* Add Story Button */}
        {currentUser && (
          <div 
            onClick={() => setCreatorOpen(true)}
            className="min-w-[100px] h-[150px] rounded-xl glass-panel relative cursor-pointer overflow-hidden flex flex-col justify-end group transition-transform hover:scale-105"
          >
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center group-hover:bg-background/30 transition-colors">
              <Icon name="Plus" size={32} className="text-primary" />
            </div>
            <div className="absolute bottom-2 left-0 w-full text-center">
              <span className="text-[10px] font-label-caps text-primary">Add Story</span>
            </div>
          </div>
        )}

        {/* Story Bubbles */}
        {activeStories?.map((group) => (
          <div 
            key={group.userId} 
            onClick={() => { setActiveStoryGroup(group); setViewerOpen(true); }}
            className="min-w-[100px] h-[150px] rounded-xl relative cursor-pointer overflow-hidden group transition-transform hover:scale-105"
          >
            <img src={group.stories[0]?.mediaUrl || group.user.avatarUrl} alt="Story cover" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent"></div>
            <div className="absolute inset-0 border-2 border-primary/50 rounded-xl pointer-events-none group-hover:border-primary transition-colors"></div>
            <div className="absolute bottom-2 left-2 flex items-center gap-2">
              <img src={group.user.avatarUrl || 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6'} alt="avatar" className="w-6 h-6 rounded-full rim-light object-cover" />
              <span className="text-xs font-bold text-primary truncate max-w-[50px]">{group.user.fullName?.split(' ')[0]}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Creator Modal */}
      {creatorOpen && (
        <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="glass-panel p-6 rounded-xl w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-headline-sm text-primary">Create Story</h3>
              <button onClick={() => setCreatorOpen(false)}><Icon name="X" className="text-on-surface-variant hover:text-primary" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-label-caps text-on-surface-variant mb-2 block">Media URL</label>
                <input 
                  type="text" 
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="Paste image or video URL..."
                  className="w-full bg-background border border-white/10 rounded-lg p-3 text-primary focus:border-primary/50 outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-label-caps text-on-surface-variant mb-2 block">Media Type</label>
                <select 
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  className="w-full bg-background border border-white/10 rounded-lg p-3 text-primary focus:border-primary/50 outline-none"
                >
                  <option value="photo">Photo</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <button 
                onClick={handleCreate}
                disabled={uploading || !mediaUrl}
                className="w-full bg-primary text-on-primary font-label-caps py-3 rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {uploading ? 'Posting...' : 'Post Story'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Viewer Modal */}
      {viewerOpen && activeStoryGroup && (
        <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-xl flex items-center justify-center">
          <button onClick={() => setViewerOpen(false)} className="absolute top-6 right-6 z-10 p-2 glass-panel rounded-full hover:bg-white/10">
            <Icon name="X" className="text-primary" />
          </button>
          
          <div className="w-full max-w-lg aspect-[9/16] relative bg-black rounded-xl overflow-hidden shadow-2xl">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
              <img src={activeStoryGroup.user.avatarUrl || 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6'} alt="avatar" className="w-10 h-10 rounded-full rim-light object-cover" />
              <div>
                <p className="font-bold text-primary">{activeStoryGroup.user.fullName}</p>
                <p className="text-xs text-white/50">{new Date(activeStoryGroup.stories[0]?.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
              </div>
            </div>

            {/* Simple viewer (just shows the first story for MVP) */}
            {activeStoryGroup.stories[0]?.mediaType === 'video' ? (
              <video src={activeStoryGroup.stories[0]?.mediaUrl} autoPlay loop controls className="w-full h-full object-contain" />
            ) : (
              <img src={activeStoryGroup.stories[0]?.mediaUrl} alt="Story content" className="w-full h-full object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoriesBar;
